import { Theme } from '@emotion/react'
import styled from '@emotion/styled'
import { StyledButton } from '../../../../buttons/Button/Button.styled'
import { colors } from '../../../../__styling/settings/colors'
import {
    breakpoint,
    breakpointMax,
} from '../../../../__styling/style-functions'
import { calcRem } from '../../../../__styling/style-functions/calc-rem'
import { fluidTypography } from '../../../../__styling/style-mixins'
import { themedValue } from '../../../../__styling/themed-value'

export type PackageTheme = 'default' | 'red' | 'yellow'
export interface PackageThemeProps {
    packageTheme: PackageTheme
}

export const StyledSubscriptionPackage = styled('div')<PackageThemeProps>(
    ({ theme }) => ({
        flexBasis: calcRem(278),
        flexShrink: 1,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        padding: calcRem(theme.margins.md, 24),
        margin: calcRem(theme.margins.md),
        borderRadius: 6,
        fontFamily: theme.fonts.subscriber.text,
        background:
            'linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, rgba(191, 205, 216, 0.5) 100%), #FFF;',
        color: themedValue(theme, {
            thewest: colors.thewest.westblue,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.sevennews.red,
        }),
        boxShadow: '0 2px 21px 0 rgba(0,0,0,0.5)',
        [breakpointMax('xxs')]: {
            margin: calcRem(theme.margins.md, 0),
            flexBasis: calcRem(250),
        },
    }),
)

interface StyledPackageImageProps {
    isRegional?: boolean
}

export const StyledPackageImage = styled('img')<StyledPackageImageProps>(
    ({ isRegional }) => ({
        width: '95%',
        display: 'block',
        margin: '0 auto',
        marginTop: isRegional ? calcRem(25) : 0,
        transform: 'scale(1.1)',
        height: '180px',
        objectFit: 'contain',
    }),
)

export const StyledSubscriptionPackageHeading = styled('h4')<PackageThemeProps>(
    ({ theme }) => ({
        fontSize: calcRem(24),
        fontWeight: 700,
        lineHeight: 1.167,
        margin: calcRem(theme.margins.md, 0, theme.margins.sm, 0),
        color: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.greyBasalt90,
            sevennews: colors.sevennews.charade,
        }),

        flex: '0 0 auto', // fixes ie11 column wrap bug
    }),
)

export const StyledSubscriptionPackageOriginalPrice = styled(
    'h4',
)<PackageThemeProps>(({ theme }) => ({
    fontSize: calcRem(16),
    fontWeight: 700,
    lineHeight: 1.167,
    textDecoration: 'line-through',
    margin: 0,
    color: themedValue(theme, {
        thewest: colors.thewest.greyGorilla,
        perthnow: colors.perthnow.greyBasalt90,
        sevennews: colors.sevennews.charade,
    }),

    flex: '0 0 auto', // fixes ie11 column wrap bug

    [breakpointMax('xs')]: {
        fontSize: calcRem(16),
    },
}))

const themedPriceFluid = (theme: Theme) =>
    themedValue(theme, {
        sevennews: fluidTypography(375, 1000, 24, 28),
        perthnow: fluidTypography(375, 1020, 24, 30),
        thewest: fluidTypography(375, 1220, 24, 24),
    })

export const StyledSubscriptionPackagePrice = styled('p')<PackageThemeProps>(
    ({ theme, packageTheme }) => ({
        ...themedPriceFluid(theme),
        flex: '0 0 auto', // fixes ie11 column wrap bug
        fontWeight: 500,
        lineHeight: 1.167,
        margin: 0,
        color: theme.sectionValues.primaryColor,
    }),
)

export const StyledSubscriptionPackagePriceQualifier = styled(
    'p',
)<PackageThemeProps>(({ theme, packageTheme }) => ({
    fontSize: calcRem(14),
    flex: '0 0 auto', // fixes ie11 column wrap bug
    fontWeight: 700,
    lineHeight: 1.167,
    margin: 0,
    color: packageTheme === 'red' ? colors.thewest.ladybird : packageTheme === 'yellow' ? colors.thewest.sunshineYellow : undefined,
}))

export const StyledSubscriptionPackageActions = styled('div')(({ theme }) => ({
    width: '100%',
    alignSelf: 'center',
    textAlign: 'center',
}))

export const StyledSubscriptionPackageButton = styled(
    StyledButton, //target styledbutton
)<PackageThemeProps>(({ theme, packageTheme }) => ({
    margin: calcRem(theme.margins.md, 0, 0),
    width: calcRem(200),
    height: calcRem(40),
    fontSize: calcRem(16),

    [breakpoint('xxs')]: {
        fontSize: calcRem(18),
        width: calcRem(230),
    },

    backgroundColor:
        packageTheme === 'red' ? colors.thewest.ladybird : packageTheme === 'yellow' ? colors.thewest.sunshineYellow : undefined,
}))

export const StyledSubscriptionCancellationMessage = styled(
    'p',
)<PackageThemeProps>(({ theme }) => ({
    fontSize: calcRem(12),
    flex: '0 0 auto', // fixes ie11 column wrap bug
    fontWeight: 400,
    lineHeight: 1.167,
    margin: 'auto 0 0',
    color: themedValue(theme, {
        thewest: colors.thewest.greyRhino,
        perthnow: colors.perthnow.greyBasalt90,
        sevennews: colors.sevennews.charade,
    }),
    paddingTop: calcRem(theme.margins.sm),
}))

export const Ribbon = styled('p')<PackageThemeProps>(
    ({ theme, packageTheme }) => ({
        position: 'absolute',
        top: 0,
        right: 0,
        background:
            packageTheme === 'red'
                ? colors.thewest.ladybird
                : packageTheme === 'yellow' ? colors.thewest.sunshineYellow 
                : 'linear-gradient(45deg, #aa01e1 0%,#f400ab 100%)',
        transform: 'rotate(45deg) translate(15%,-100%)',
        height: 80,
        width: 160,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        textTransform: 'uppercase',
        fontSize: 10,
        fontWeight: 700,
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.5)',

        span: {
            color: colors.white,
            display: 'block',
            width: 50,
            textAlign: 'center',
            lineHeight: 1,
            marginBottom: theme.margins.xs,
        },

        svg: {
            position: 'absolute',
            left: '50%',
            transform: 'translate(-50%, calc(-100% - 5px))',
        },
    }),
)

export const StyledSubscribeWithGoogleButton = styled('button')(
    ({ theme }) => ({
        marginTop: theme.margins.sm,
        cursor: 'pointer',
        maxWidth: '100%',
        borderRadius: '4px',
    }),
)
